import React, { useState, useEffect, useCallback } from 'react';
import { Search, AlertCircle, ArrowLeft, ArrowRight, CheckSquare, Square, ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import { manualDrawApi } from '../../../../services/api';
import CSVImportModal from './CSVImportModal';

interface Lead {
  id: string;
  customer_name: string;
  whatsapp: string;
  customer_birthdate: string | null;
  last_wheel_name: string;
  created_at: string;
}

interface LeadSelectionProps {
  selectedLeads: Lead[];
  onLeadsSelected: (leads: Lead[]) => void;
  onBack: () => void;
}

const LeadSelection: React.FC<LeadSelectionProps> = ({ selectedLeads, onLeadsSelected, onBack }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<string[]>(selectedLeads.map(l => l.id));
  const [selectedLeadsData, setSelectedLeadsData] = useState<Lead[]>(selectedLeads);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  const itemsPerPage = 10;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await manualDrawApi.getAvailableLeads({
        search: debouncedSearch,
        page: currentPage,
        limit: itemsPerPage
      });
      setLeads(response.leads);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalCount(response.pagination?.total || response.leads.length);

      // Update selectAll state based on current page
      if (response.leads.length > 0) {
        const allCurrentPageSelected = response.leads.every((lead: Lead) => selected.includes(lead.id));
        setSelectAll(allCurrentPageSelected);
      } else {
        setSelectAll(false);
      }
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, selected]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);


  const toggleLead = (lead: Lead) => {
    if (selected.includes(lead.id)) {
      // Remove from selection
      setSelected(selected.filter(id => id !== lead.id));
      setSelectedLeadsData(selectedLeadsData.filter(l => l.id !== lead.id));
      setSelectAll(false);
    } else {
      // Add to selection
      setSelected([...selected, lead.id]);
      setSelectedLeadsData([...selectedLeadsData, lead]);

      // Check if all current page leads are now selected
      const allCurrentPageSelected = leads.every(l =>
        l.id === lead.id || selected.includes(l.id)
      );
      setSelectAll(allCurrentPageSelected);
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      // Deselect all from current page
      const currentPageIds = leads.map(l => l.id);
      setSelected(selected.filter(id => !currentPageIds.includes(id)));
      setSelectedLeadsData(selectedLeadsData.filter(l => !currentPageIds.includes(l.id)));
      setSelectAll(false);
    } else {
      // Select all from current page
      const newSelections = leads.filter(lead => !selected.includes(lead.id));
      setSelected([...selected, ...newSelections.map(l => l.id)]);
      setSelectedLeadsData([...selectedLeadsData, ...newSelections]);
      setSelectAll(true);
    }
  };

  const handleContinue = () => {
    onLeadsSelected(selectedLeadsData);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleImportSuccess = () => {
    loadLeads(); // Reload leads after import
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '—';
    // Format: (99) 99999-9999
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bordo"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-tinta leading-tight">
          Selecione os <span className="italic text-bordo">participantes</span>
        </h2>
        <p className="mt-1 text-xs sm:text-sm text-tinta-soft italic">
          Escolha os leads para participar
        </p>
      </div>

      {/* Search and Import */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tinta-soft w-4 h-4 sm:w-5 sm:h-5" />
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-tinta/15 rounded-lg text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
          />
          {loading && debouncedSearch !== search && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-bordo"></div>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowImportModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-bordo/8 text-bordo border border-bordo/20 hover:bg-bordo/15 transition-colors whitespace-nowrap"
        >
          <Upload className="w-4 h-4" />
          Importar CSV
        </button>
      </div>

      {/* Selected Count & Select All */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-creme-light border border-tinta/10 rounded-lg p-3">
        <button
          onClick={toggleSelectAll}
          disabled={loading || leads.length === 0}
          className="flex items-center gap-2 text-xs sm:text-sm font-bold text-tinta hover:text-bordo disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {selectAll ? (
            <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-bordo flex-shrink-0" />
          ) : (
            <Square className="w-4 h-4 sm:w-5 sm:h-5 text-tinta-soft flex-shrink-0" />
          )}
          <span className="uppercase tracking-wider text-xs">Selecionar todos desta página</span>
        </button>
        {selected.length > 0 && (
          <span className="bg-mostarda/20 text-mostarda-dark border border-mostarda/40 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider self-start sm:self-auto">
            {selected.length} selecionado{selected.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bordo"></div>
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-10 italic text-tinta-soft">
          <AlertCircle className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-tinta-soft/50 mb-2 sm:mb-3 not-italic" />
          <p className="text-sm sm:text-base">Nenhum lead encontrado</p>
        </div>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="sm:hidden space-y-2">
            {leads.map((lead) => {
              const isSelected = selected.includes(lead.id);
              return (
                <div
                  key={lead.id}
                  onClick={() => toggleLead(lead)}
                  className={`rounded-lg p-3 cursor-pointer transition-all ${isSelected
                      ? 'bg-bordo/8 border-2 border-bordo'
                      : 'bg-white border border-tinta/15 hover:border-tinta/30'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-sm text-tinta truncate">{lead.customer_name}</h3>
                      <p className="text-xs text-tinta-soft mt-0.5">{formatPhone(lead.whatsapp)}</p>
                      {lead.last_wheel_name && (
                        <p className="text-xs text-tinta-soft/80 mt-1 truncate italic">Último: {lead.last_wheel_name}</p>
                      )}
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${isSelected
                        ? 'bg-bordo border-bordo'
                        : 'border-tinta/30 bg-white'
                      }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-creme" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block border border-tinta/10 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-tinta/10">
                <thead className="bg-creme-light">
                  <tr>
                    <th className="w-12 px-4 py-3"></th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-tinta uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-tinta uppercase tracking-wider">
                      WhatsApp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-tinta uppercase tracking-wider">
                      Aniversário
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-tinta uppercase tracking-wider">
                      Último Sorteio
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-tinta/10">
                  {leads.map((lead) => {
                    const isSelected = selected.includes(lead.id);
                    return (
                      <tr
                        key={lead.id}
                        onClick={() => toggleLead(lead)}
                        className={`cursor-pointer transition-colors ${isSelected ? 'bg-bordo/8' : 'hover:bg-creme-light'
                          }`}
                      >
                        <td className="px-4 py-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected
                              ? 'bg-bordo border-bordo'
                              : 'border-tinta/30 bg-white'
                            }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-creme" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-tinta">
                          {lead.customer_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-tinta-soft">
                          {formatPhone(lead.whatsapp)}
                        </td>
                        <td className="px-4 py-3 text-sm text-tinta-soft">
                          {formatDate(lead.customer_birthdate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-tinta-soft italic">
                          {lead.last_wheel_name || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 border-t border-tinta/10">
          <p className="text-xs sm:text-sm text-tinta-soft italic">
            Página <span className="font-bold text-tinta not-italic">{currentPage}</span> de <span className="font-bold text-tinta not-italic">{totalPages}</span> ({totalCount} participantes)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 text-tinta-soft border border-tinta/15 rounded-lg hover:bg-creme-light hover:text-tinta disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 text-tinta-soft border border-tinta/15 rounded-lg hover:bg-creme-light hover:text-tinta disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 mt-6 border-t border-tinta/10">
        <button
          onClick={onBack}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft hover:bg-creme-light hover:text-tinta transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
        <button
          onClick={handleContinue}
          disabled={selected.length === 0}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuar ({selected.length})
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={handleImportSuccess}
      />
    </div>
  );
};

export default LeadSelection;
